import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type ThoughtBubbleStorageService from './thought-bubble-storage';

export type BuilderMessage =
  | {
      type: 'thought-bubble-step';
      flowId: string;
      step: {
        elementSelector: string;
        title: string;
        description: string;
        side: string;
        align: string;
        disableActiveInteraction: boolean;
        order: number;
      };
    }
  | { type: 'thought-bubble-builder-close'; flowId: string }
  | { type: 'tb-builder-ready'; flowId: string };

/**
 * Builder service for the Thought Bubble visual element picker.
 *
 * This service intentionally uses imperative DOM manipulation (createElement,
 * innerHTML, event listeners) rather than Ember components/modifiers because it
 * operates as a devtools-style overlay injected onto arbitrary pages. The overlay
 * panel and highlight elements exist outside the host app's Ember render tree and
 * cannot leverage its component lifecycle. This is similar to how Ember Inspector
 * or browser DevTools inject UI into a running page.
 */
export default class ThoughtBubbleBuilderService extends Service {
  @service declare thoughtBubbleStorage: ThoughtBubbleStorageService;

  @tracked isActive = false;
  @tracked currentFlowId: string | null = null;
  @tracked mode: 'pick' | 'config' = 'pick';
  @tracked selectedSelector: string | null = null;
  @tracked builderWindow: Window | null = null;

  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private overlay: HTMLDivElement | null = null;
  private panel: HTMLDivElement | null = null;
  private openerOrigin: string | null = null;

  checkActivation(): void {
    const params = new URLSearchParams(window.location.search);
    const flowId = params.get('tb-builder');
    if (flowId) {
      this.currentFlowId = flowId;
      this.activate();

      // Derive the opener's origin from document.referrer for a targeted postMessage.
      // Falls back to window.location.origin if referrer is unavailable.
      const openerOrigin = document.referrer
        ? new URL(document.referrer).origin
        : window.location.origin;
      this.openerOrigin = openerOrigin;

      // Notify the opener (admin) that the builder is ready to receive messages
      window.opener?.postMessage({ type: 'tb-builder-ready', flowId }, openerOrigin);
    }
  }

  activate(): void {
    if (this.isActive) return;
    this.isActive = true;
    this.createOverlay();
    this.createPanel();
    this.setupMessageListener();
  }

  deactivate(): void {
    if (this.currentFlowId && this.openerOrigin) {
      window.opener?.postMessage(
        { type: 'thought-bubble-builder-close', flowId: this.currentFlowId },
        this.openerOrigin
      );
    }
    this.isActive = false;
    this.currentFlowId = null;
    this.mode = 'pick';
    this.selectedSelector = null;
    this.removeOverlay();
    this.removePanel();
    this.removeMessageListener();
  }

  willDestroy(): void {
    super.willDestroy();
    this.removeOverlay();
    this.removePanel();
    this.removeMessageListener();
    document.querySelector('.tb-highlight')?.remove();
  }

  launchBuilderWindow(url: string, flowId: string): void {
    this.currentFlowId = flowId;
    const separator = url.includes('?') ? '&' : '?';
    const builderUrl = `${url}${separator}tb-builder=${flowId}`;
    this.builderWindow = window.open(builderUrl, '_blank');
  }

  private setupMessageListener(): void {
    this.messageHandler = (event: MessageEvent) => {
      // Validate message source (postMessage guarantees event.source cannot be spoofed).
      // Origin check is intentionally omitted: admin (:4250) and employee (:4251) are
      // cross-origin by design, so source-based validation is the correct strategy.
      const isFromOpener = !!window.opener && event.source === window.opener;
      const isFromBuilderWindow = !!this.builderWindow && event.source === this.builderWindow;
      if (!(isFromOpener || isFromBuilderWindow)) {
        return;
      }

      // Track the opener's origin so we can address outgoing messages correctly
      if (isFromOpener && !this.openerOrigin) {
        this.openerOrigin = event.origin;
      }

      if (event.data?.type === 'tb-step-captured') {
        this.handleStepCaptured(event.data);
      } else if (event.data?.type === 'tb-sync-flow' && event.data.flow) {
        // Cross-origin flow sync: admin sends flow definition to builder window.
        // Only cache locally — the admin page manages persistence.
        this.thoughtBubbleStorage.cacheFlowLocally(event.data.flow);
      } else if (event.data?.type === 'tb-builder-ready') {
        if (event.source && 'postMessage' in event.source) {
          (event.source as Window).postMessage(
            { type: 'tb-builder-init', flowId: this.currentFlowId },
            event.origin
          );
        }
      }
    };
    window.addEventListener('message', this.messageHandler);
  }

  private removeMessageListener(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  }

  private pendingSelector = '';
  private pendingText = '';

  private handleStepCaptured(data: {
    selector: string;
    text?: string;
    tagName?: string;
    rect?: { top: number; left: number; width: number; height: number };
  }): void {
    if (!this.currentFlowId) return;

    // Store pending data and switch to config mode to show the form
    this.pendingSelector = data.selector;
    this.pendingText = data.text?.slice(0, 50) || '';
    this.mode = 'config';
    this.showConfigForm();
  }

  private sendStep(): void {
    if (!this.currentFlowId) return;

    const title =
      (this.panel?.querySelector('#tb-title') as HTMLInputElement)?.value ||
      this.pendingText ||
      'Untitled Step';
    const description = (this.panel?.querySelector('#tb-desc') as HTMLTextAreaElement)?.value || '';
    const side = (this.panel?.querySelector('#tb-side') as HTMLSelectElement)?.value || 'bottom';
    const align = (this.panel?.querySelector('#tb-align') as HTMLSelectElement)?.value || 'start';

    const step = {
      elementSelector: this.pendingSelector,
      title,
      description,
      side,
      align,
      disableActiveInteraction: false,
      order: 0,
    };

    window.opener?.postMessage(
      { type: 'thought-bubble-step', step, flowId: this.currentFlowId },
      this.openerOrigin || window.location.origin
    );

    // Reset to pick mode for next element
    this.mode = 'pick';
    this.showPickPanel();
  }

  private createOverlay(): void {
    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      zIndex: '99998',
      pointerEvents: 'none',
    });
    document.body.appendChild(this.overlay);

    // Track mouse for element highlighting
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('click', this.onClick, true);
  }

  private removeOverlay(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('click', this.onClick, true);
  }

  private createPanel(): void {
    this.panel = document.createElement('div');
    this.applyPanelStyles();
    document.body.appendChild(this.panel);
    this.showPickPanel();
  }

  private applyPanelStyles(): void {
    if (!this.panel) return;
    Object.assign(this.panel.style, {
      position: 'fixed',
      bottom: '16px',
      right: '16px',
      width: '320px',
      maxHeight: '80vh',
      overflowY: 'auto',
      padding: '16px',
      background: '#1a1a2e',
      color: '#e0e0e0',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      zIndex: '99999',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
    });
  }

  private showPickPanel(): void {
    if (!this.panel) return;
    // Remove existing highlight
    document.querySelector('.tb-highlight')?.remove();

    this.panel.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <strong style="font-size:14px">🫧 Thought Bubble Builder</strong>
        <button id="tb-close" style="background:none;border:none;color:#e0e0e0;cursor:pointer;font-size:16px">✕</button>
      </div>
      <p style="margin:0 0 8px;opacity:0.7">Click on any element to capture its selector as a step.</p>
      <div id="tb-status" style="padding:8px;background:rgba(255,255,255,0.1);border-radius:6px;margin-top:8px">Ready — hover over elements</div>
    `;
    this.panel.querySelector('#tb-close')?.addEventListener('click', () => {
      this.deactivate();
    });
  }

  private showConfigForm(): void {
    if (!this.panel) return;
    // Remove element highlight while configuring
    document.querySelector('.tb-highlight')?.remove();

    const escapedSelector = this.pendingSelector
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const labelStyle = 'display:block;margin:0 0 4px;font-weight:600;font-size:12px;opacity:0.8';
    const inputStyle =
      'width:100%;padding:6px 8px;background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.2);border-radius:6px;color:#e0e0e0;font-size:13px;font-family:inherit;box-sizing:border-box';
    const selectStyle = `${inputStyle};appearance:auto`;
    const fieldGap = 'margin-bottom:10px';

    this.panel.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <strong style="font-size:14px">🫧 Step Details</strong>
        <button id="tb-close" style="background:none;border:none;color:#e0e0e0;cursor:pointer;font-size:16px">✕</button>
      </div>
      <div style="padding:6px 8px;background:rgba(108,92,231,0.2);border:1px solid rgba(108,92,231,0.4);border-radius:6px;margin-bottom:12px;word-break:break-all;font-size:11px;font-family:monospace">
        ${escapedSelector}
      </div>
      <div style="${fieldGap}">
        <label style="${labelStyle}">Title</label>
        <input id="tb-title" type="text" value="" placeholder="Step title" style="${inputStyle}" />
      </div>
      <div style="${fieldGap}">
        <label style="${labelStyle}">Description</label>
        <textarea id="tb-desc" rows="2" placeholder="Optional description" style="${inputStyle};resize:vertical"></textarea>
      </div>
      <div style="display:flex;gap:8px;${fieldGap}">
        <div style="flex:1">
          <label style="${labelStyle}">Side</label>
          <select id="tb-side" style="${selectStyle}">
            <option value="bottom" selected>Bottom</option>
            <option value="top">Top</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </div>
        <div style="flex:1">
          <label style="${labelStyle}">Align</label>
          <select id="tb-align" style="${selectStyle}">
            <option value="start" selected>Start</option>
            <option value="center">Center</option>
            <option value="end">End</option>
          </select>
        </div>
      </div>
      <div style="display:flex;gap:8px;margin-top:4px">
        <button id="tb-add-step" style="flex:1;padding:8px;background:#6c5ce7;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:600;font-size:13px">Add Step</button>
        <button id="tb-pick-another" style="padding:8px 12px;background:rgba(255,255,255,0.1);color:#e0e0e0;border:1px solid rgba(255,255,255,0.2);border-radius:6px;cursor:pointer;font-size:13px">Pick Another</button>
      </div>
    `;

    this.panel.querySelector('#tb-close')?.addEventListener('click', () => {
      this.deactivate();
    });
    this.panel.querySelector('#tb-add-step')?.addEventListener('click', () => {
      this.sendStep();
    });
    this.panel.querySelector('#tb-pick-another')?.addEventListener('click', () => {
      this.mode = 'pick';
      this.showPickPanel();
    });
    // Set title value programmatically to avoid innerHTML injection
    const titleInput = this.panel.querySelector('#tb-title') as HTMLInputElement;
    if (titleInput) {
      titleInput.value = this.pendingText;
      titleInput.focus();
    }
  }

  private removePanel(): void {
    if (this.panel) {
      this.panel.remove();
      this.panel = null;
    }
  }

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.isActive || this.mode !== 'pick') return;
    const target = e.target as HTMLElement;
    if (this.panel?.contains(target)) return;

    // Highlight element
    const existing = document.querySelector('.tb-highlight');
    if (existing) existing.remove();

    const rect = target.getBoundingClientRect();
    const highlight = document.createElement('div');
    highlight.className = 'tb-highlight';
    Object.assign(highlight.style, {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      border: '2px solid #6c5ce7',
      background: 'rgba(108, 92, 231, 0.1)',
      pointerEvents: 'none',
      zIndex: '99998',
      borderRadius: '4px',
      transition: 'all 0.1s ease',
    });
    document.body.appendChild(highlight);

    // Update status
    const selector = this.generateSelector(target);
    const status = this.panel?.querySelector('#tb-status');
    if (status) {
      status.textContent = selector;
    }
  };

  private onClick = (e: MouseEvent): void => {
    if (!this.isActive || this.mode !== 'pick') return;
    const target = e.target as HTMLElement;
    if (this.panel?.contains(target)) return;

    e.preventDefault();
    e.stopPropagation();

    const selector = this.generateSelector(target);
    const text = target.textContent?.trim() || '';

    this.handleStepCaptured({
      selector,
      text,
      tagName: target.tagName.toLowerCase(),
      rect: target.getBoundingClientRect(),
    });
  };

  private generateSelector(el: HTMLElement): string {
    // 1. Prefer explicit data-tour attribute (most stable)
    if (el.dataset['tour']) return `[data-tour="${el.dataset['tour']}"]`;

    // 2. Unique id (skip Ember-generated ids)
    if (el.id && !this.isUnstableId(el.id) && this.isUnique(`#${el.id}`)) {
      return `#${el.id}`;
    }

    // 3. data-test attribute (exact match)
    if (el.dataset['test']) return `[data-test="${el.dataset['test']}"]`;

    // 4. Any data-test-* or data-* attribute that makes a unique selector
    for (const [key, value] of Object.entries(el.dataset)) {
      if (key === 'tour') continue;
      const attr = `data-${this.camelToKebab(key)}`;
      const selector = value ? `[${attr}="${value}"]` : `[${attr}]`;
      if (this.isUnique(selector)) return selector;
    }

    // 5. For form inputs: name attribute is usually stable and unique
    if (this.isFormElement(el)) {
      const name = el.getAttribute('name');
      if (name) {
        const selector = `${el.tagName.toLowerCase()}[name="${name}"]`;
        if (this.isUnique(selector)) return selector;
      }
    }

    // 6. If element has a data-test-* attr that isn't unique on its own,
    //    scope it under the nearest ancestor with a data-test-* attr
    const scopedSelector = this.buildScopedDataTestSelector(el);
    if (scopedSelector) return scopedSelector;

    // 7. aria-label (good for buttons/links)
    const ariaLabel = el.getAttribute('aria-label');
    if (ariaLabel) {
      const selector = `${el.tagName.toLowerCase()}[aria-label="${ariaLabel}"]`;
      if (this.isUnique(selector)) return selector;
    }

    // 8. role + meaningful class combo
    const role = el.getAttribute('role');
    const stableClass = this.getStableClass(el);
    if (role && stableClass) {
      const selector = `[role="${role}"].${stableClass}`;
      if (this.isUnique(selector)) return selector;
    }

    // 9. Tag + stable class (unique)
    if (stableClass) {
      const selector = `${el.tagName.toLowerCase()}.${stableClass}`;
      if (this.isUnique(selector)) return selector;
    }

    // 10. Shortest unique ancestor path using stable anchors
    return this.buildSelectorPath(el);
  }

  /**
   * Check if a CSS selector matches exactly one element in the document.
   */
  private isUnique(selector: string): boolean {
    try {
      return document.querySelectorAll(selector).length === 1;
    } catch {
      return false;
    }
  }

  /**
   * Check if an element is a form input (input, select, textarea).
   */
  private isFormElement(el: HTMLElement): boolean {
    return ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName);
  }

  /**
   * Check if an ID looks like an Ember/Glimmer auto-generated ID.
   * These IDs change across page loads and are never deterministic.
   */
  private isUnstableId(id: string): boolean {
    return /ember\d+/.test(id);
  }

  /**
   * Get the first data-test-* attribute selector for an element.
   * Returns the attribute selector string (e.g., '[data-test-search-input]')
   * or null if no data-test-* attributes exist.
   */
  private getDataTestSelector(el: HTMLElement): string | null {
    for (const attr of el.attributes) {
      if (attr.name.startsWith('data-test')) {
        return attr.value ? `[${attr.name}="${attr.value}"]` : `[${attr.name}]`;
      }
    }
    return null;
  }

  /**
   * Find the nearest ancestor with a data-test-* attribute and build
   * a scoped selector: 'ancestor-selector element-selector'.
   * This disambiguates elements like [data-test-input] that appear multiple
   * times on a page by scoping them under a unique parent.
   *
   * When simple scoping isn't enough (e.g., multiple [data-test-select] on one
   * page), uses nth-of-type on the ancestor's parent to disambiguate.
   */
  private buildScopedDataTestSelector(el: HTMLElement): string | null {
    const elSelector = this.getDataTestSelector(el);
    if (!elSelector) return null;

    let ancestor: HTMLElement | null = el.parentElement;
    const maxDepth = 6;
    let depth = 0;

    while (ancestor && ancestor !== document.body && depth < maxDepth) {
      const ancestorSelector = this.getDataTestSelector(ancestor);
      if (ancestorSelector) {
        const scoped = `${ancestorSelector} ${elSelector}`;
        if (this.isUnique(scoped)) return scoped;

        // Scoped selector matches multiple elements. Try adding nth-of-type
        // to the ancestor to disambiguate (e.g., the 2nd [data-test-select]).
        const positional = this.addPositionalIndex(ancestor, ancestorSelector);
        if (positional) {
          const scopedPositional = `${positional} ${elSelector}`;
          if (this.isUnique(scopedPositional)) return scopedPositional;
        }
      }

      // Also try ancestor's name attribute (for named form containers)
      const name = ancestor.getAttribute('name');
      if (name) {
        const scoped = `${ancestor.tagName.toLowerCase()}[name="${name}"] ${elSelector}`;
        if (this.isUnique(scoped)) return scoped;
      }

      ancestor = ancestor.parentElement;
      depth++;
    }

    // If element has a name attribute, try scoping by ancestor + name
    if (this.isFormElement(el)) {
      const name = el.getAttribute('name');
      if (name) {
        ancestor = el.parentElement;
        depth = 0;
        while (ancestor && ancestor !== document.body && depth < maxDepth) {
          const ancestorSelector = this.getDataTestSelector(ancestor);
          if (ancestorSelector) {
            const scoped = `${ancestorSelector} ${el.tagName.toLowerCase()}[name="${name}"]`;
            if (this.isUnique(scoped)) return scoped;
          }
          ancestor = ancestor.parentElement;
          depth++;
        }
      }
    }

    return null;
  }

  /**
   * Build a positional selector for an element among its siblings that
   * share the same data-test-* attribute. For example, if there are 3
   * [data-test-select] divs inside a list, this returns
   * '[data-test-select]:nth-of-type(2)' for the second one — but only
   * if the parent wraps each in a consistent container (like <li>).
   *
   * First tries nth-of-type on matching siblings with the same tag+attr.
   * Falls back to scoping under the parent element (e.g., li:nth-of-type(2) [data-test-select]).
   */
  private addPositionalIndex(el: HTMLElement, baseSelector: string): string | null {
    const parent = el.parentElement;
    if (!parent) return null;

    // Strategy 1: nth-of-type among siblings with the same tag name
    const sameTagSiblings = Array.from(parent.children).filter((c) => c.tagName === el.tagName);
    if (sameTagSiblings.length > 1) {
      const index = sameTagSiblings.indexOf(el) + 1;
      const positional = `${baseSelector}:nth-of-type(${index})`;
      if (this.isUnique(positional)) return positional;
    }

    // Strategy 2: walk up through ancestors looking for a positionally
    // indexable container (e.g., li:nth-of-type(N) [data-test-select]).
    // Uses descendant combinator (space) so intermediate wrappers don't break it.
    let container = parent;
    for (let i = 0; i < 4; i++) {
      const containerParent = container.parentElement;
      if (!containerParent) break;

      const containerSiblings = Array.from(containerParent.children).filter(
        (c) => c.tagName === container!.tagName
      );
      if (containerSiblings.length > 1) {
        const idx = containerSiblings.indexOf(container) + 1;
        const tag = container.tagName.toLowerCase();
        const positional = `${tag}:nth-of-type(${idx}) ${baseSelector}`;
        if (this.isUnique(positional)) return positional;
      }
      container = containerParent;
    }

    return null;
  }

  /**
   * Get a stable (non-Ember-generated) class name from an element.
   * Filters out classes that look auto-generated (ember-view, ember-*, random hashes).
   */
  private getStableClass(el: HTMLElement): string | null {
    const unstable = /^(ember-|_|glimmer-|e-|--)/;
    for (const cls of el.classList) {
      if (!unstable.test(cls) && cls.length > 2 && !cls.match(/^[a-z0-9]{6,}$/)) {
        return cls;
      }
    }
    return null;
  }

  /**
   * Convert camelCase dataset key to kebab-case for data attribute selectors.
   */
  private camelToKebab(str: string): string {
    return str.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  /**
   * Build a CSS selector path from an element up through its ancestors,
   * using stable anchors (data-test-*, data-tour, stable IDs, name attrs).
   * Avoids Ember-generated IDs.
   */
  private buildSelectorPath(el: HTMLElement): string {
    const parts: string[] = [];
    let current: HTMLElement | null = el;
    let depth = 0;
    const maxDepth = 8;

    while (current && current !== document.body && depth < maxDepth) {
      let segment: string | null = null;
      let isGloballyUnique = false;

      // Try stable anchors: id, data-tour, data-test-*, name
      if (current.id && !this.isUnstableId(current.id)) {
        segment = `#${current.id}`;
        isGloballyUnique = true; // IDs should be unique
      } else if (current.dataset['tour']) {
        segment = `[data-tour="${current.dataset['tour']}"]`;
        isGloballyUnique = true;
      } else {
        const dataTestSelector = this.getDataTestSelector(current);
        if (dataTestSelector) {
          segment = dataTestSelector;
        } else if (this.isFormElement(current)) {
          const name = current.getAttribute('name');
          if (name) {
            segment = `${current.tagName.toLowerCase()}[name="${name}"]`;
          }
        }
      }

      // If no anchor found, build a structural segment
      if (!segment) {
        segment = current.tagName.toLowerCase();
        const stableClass = this.getStableClass(current);
        if (stableClass) {
          segment += `.${stableClass}`;
        }

        const parent: HTMLElement | null = current.parentElement;
        if (parent) {
          const siblings = Array.from(parent.children).filter(
            (c: Element) => c.tagName === current!.tagName
          );
          if (siblings.length > 1) {
            const index = siblings.indexOf(current) + 1;
            segment += `:nth-of-type(${index})`;
          }
        }
      }

      parts.unshift(segment);

      // Check if the accumulated path is unique
      const candidate = parts.join(' > ');
      if (this.isUnique(candidate)) {
        // Try to simplify: keep only anchor segments with descendant combinator
        const anchorParts = parts.filter(
          (p) =>
            p.startsWith('[data-t') ||
            p.startsWith('#') ||
            p.includes('[name=') ||
            p.includes(':nth-of-type')
        );
        if (anchorParts.length >= 2 && anchorParts.length < parts.length) {
          const simplified = anchorParts.join(' ');
          if (this.isUnique(simplified)) {
            const simplifiedElement = document.querySelector(simplified);
            const candidateElement = document.querySelector(candidate);
            if (simplifiedElement && candidateElement && simplifiedElement === candidateElement) {
              return simplified;
            }
          }
        }
        return candidate;
      }

      // For globally unique anchors (id, data-tour), stop searching
      if (isGloballyUnique) break;

      current = current.parentElement;
      depth++;
    }

    const selector = parts.join(' > ');

    // Final safety check: if the selector still contains an unstable
    // Ember ID, warn — it will break across page loads
    if (/ember\d+/.test(selector)) {
      console.warn(
        `[thought-bubble] Captured selector contains an unstable Ember ID and may not work across page loads: ${selector}`
      );
    }

    return selector;
  }
}

declare module '@ember/service' {
  interface Registry {
    'thought-bubble-builder': ThoughtBubbleBuilderService;
  }
}
